import { useRef, useState } from 'react'
import { useStore } from '../store/AppStore'
import { usedBytes } from '../lib/storage'
import { num } from '../lib/date'
import { Button, Card, Field, Meta, ScreenHead } from '../components/ui'
import { LANGS, useT, type Lang, type TKey } from '../i18n'
import { INTAKES, type IntakeId, type ThemeMode } from '../store/types'

/** Accent choices. Each is contrast-checked against both surfaces; the
 *  foreground that sits on them is derived at runtime — see lib/color.ts. */
const ACCENTS = [
  { name: 'Signal', value: '#1539F6' },
  { name: 'Clay', value: '#c2643f' },
  { name: 'Amber', value: '#a8791f' },
  { name: 'Sage', value: '#5d7f52' },
  { name: 'Iris', value: '#6c63b5' },
  { name: 'Rose', value: '#b3547a' },
  { name: 'Teal', value: '#3f7f7d' },
]

export default function Settings({ onToast }: { onToast: (msg: string) => void }) {
  const { state, setSettings, exportJSON, importJSON, resetAll, storageBlocked, pristine } =
    useStore()
  const { settings } = state
  const t = useT()
  const fileRef = useRef<HTMLInputElement>(null)
  const [confirmReset, setConfirmReset] = useState(false)

  const download = () => {
    const blob = new Blob([exportJSON()], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ala-backup-${new Date().toISOString().slice(0, 10)}.json`
    a.click()
    URL.revokeObjectURL(url)
    onToast(t('toast.backupDownloaded'))
  }

  const upload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    file.text().then((text) => {
      const result = importJSON(text)
      onToast(
        result.ok
          ? t('toast.backupRestored')
          : t('toast.importFailed', { error: result.error ?? '' }),
      )
    })
    e.target.value = ''
  }

  const pickLang = (lang: Lang) => {
    if (lang === settings.lang) return
    setSettings({ lang })
  }

  const pickIntake = (targetIntake: IntakeId) => {
    if (targetIntake === settings.targetIntake) return
    setSettings({ targetIntake })
    const label = t(INTAKES.find((i) => i.id === targetIntake)!.labelKey as TKey)
    onToast(pristine ? t('toast.replanned', { intake: label }) : t('toast.keptYourDates'))
  }

  const kb = Math.round(usedBytes() / 102.4) / 10

  return (
    <>
      <ScreenHead title={t('settings.title')} sub={t('settings.subtitle')} />

      {storageBlocked && (
        <Card sunk style={{ marginBottom: 'var(--s-5)', borderInlineStart: '3px solid var(--danger)' }}>
          <strong style={{ fontSize: 'var(--t-sm)' }}>{t('settings.blockedTitle')}</strong>
          <p className="setting__hint">{t('settings.blockedBody')}</p>
        </Card>
      )}

      <div className="stack stack--lg">
        <Card>
          <Meta>{t('settings.you')}</Meta>
          <div className="stack" style={{ marginTop: 'var(--s-4)' }}>
            <Field label={t('settings.name')}>
              <input
                className="input"
                value={settings.name}
                dir="auto"
                onChange={(e) => setSettings({ name: e.target.value })}
              />
            </Field>
            <Field label={t('settings.city')}>
              <input
                className="input"
                value={settings.city}
                dir="auto"
                onChange={(e) => setSettings({ city: e.target.value })}
              />
            </Field>
          </div>
        </Card>

        <Card>
          <Meta>{t('settings.appearance')}</Meta>

          <div className="setting">
            <div className="grow">
              <div className="setting__label">{t('settings.language')}</div>
              <div className="setting__hint">{t('settings.languageHint')}</div>
            </div>
            <div className="row" style={{ gap: 'var(--s-1)' }}>
              {LANGS.map((l) => (
                <button
                  key={l.id}
                  className="chip"
                  lang={l.id}
                  aria-pressed={settings.lang === l.id}
                  onClick={() => pickLang(l.id)}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <div className="setting">
            <div className="grow">
              <div className="setting__label">{t('settings.theme')}</div>
              <div className="setting__hint">{t('settings.themeHint')}</div>
            </div>
            <div className="row" style={{ gap: 'var(--s-1)' }}>
              {(
                [
                  ['light', 'settings.themeLight'],
                  ['dark', 'settings.themeDark'],
                  ['system', 'settings.themeSystem'],
                ] as [ThemeMode, TKey][]
              ).map(([mode, key]) => (
                <button
                  key={mode}
                  className="chip"
                  aria-pressed={settings.themeMode === mode}
                  onClick={() => setSettings({ themeMode: mode })}
                >
                  {t(key)}
                </button>
              ))}
            </div>
          </div>

          <div className="setting">
            <div className="grow">
              <div className="setting__label">{t('settings.accent')}</div>
              <div className="setting__hint">{t('settings.accentHint')}</div>
            </div>
            <div className="row row--wrap" style={{ gap: 'var(--s-2)', justifyContent: 'flex-end' }}>
              {ACCENTS.map((a) => (
                <button
                  key={a.value}
                  className="swatch"
                  style={{ background: a.value }}
                  aria-label={a.name}
                  aria-pressed={settings.accent.toLowerCase() === a.value.toLowerCase()}
                  onClick={() => setSettings({ accent: a.value })}
                />
              ))}
            </div>
          </div>
        </Card>

        <Card>
          <Meta>{t('settings.plan')}</Meta>
          <div className="setting" style={{ borderBottom: 'none' }}>
            <div className="grow">
              <div className="setting__label">{t('settings.intake')}</div>
              <div className="setting__hint">{t('settings.intakeHint')}</div>
            </div>
          </div>
          <div className="row row--wrap" style={{ gap: 'var(--s-2)' }}>
            {INTAKES.map((i) => (
              <button
                key={i.id}
                className="chip"
                aria-pressed={settings.targetIntake === i.id}
                onClick={() => pickIntake(i.id)}
              >
                {t(i.labelKey as TKey)}
              </button>
            ))}
          </div>
        </Card>

        <Card>
          <Meta>{t('settings.focusTimer')}</Meta>
          {(
            [
              ['focusMins', 'settings.focusBlock', 5, 90],
              ['breakMins', 'settings.break', 1, 30],
            ] as ['focusMins' | 'breakMins', TKey, number, number][]
          ).map(([key, labelKey, min, max]) => (
            <div className="setting" key={key}>
              <div className="grow">
                <div className="setting__label">{t(labelKey)}</div>
              </div>
              <input
                type="range"
                min={min}
                max={max}
                step={key === 'focusMins' ? 5 : 1}
                value={settings[key]}
                onChange={(e) => setSettings({ [key]: Number(e.target.value) })}
                aria-label={t('settings.timerLength', { label: t(labelKey) })}
                style={{ width: 130, accentColor: 'var(--accent)' }}
              />
              <span className="countdown" style={{ width: 56, textAlign: 'end' }}>
                {num(settings[key])}
              </span>
            </div>
          ))}
        </Card>

        <Card>
          <Meta>{t('settings.yourData')}</Meta>
          <p className="setting__hint" style={{ marginTop: 'var(--s-2)' }}>
            {t('settings.dataHint', { kb: num(kb) })}
          </p>
          <div className="row row--wrap" style={{ marginTop: 'var(--s-4)', gap: 'var(--s-2)' }}>
            <Button variant="ghost" onClick={download}>
              {t('settings.export')}
            </Button>
            <Button variant="ghost" onClick={() => fileRef.current?.click()}>
              {t('settings.import')}
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept="application/json"
              className="sr-only"
              onChange={upload}
              aria-label={t('settings.chooseBackup')}
            />
          </div>

          <div
            className="setting"
            style={{
              marginTop: 'var(--s-4)',
              borderTop: '1px solid var(--line)',
              borderBottom: 'none',
            }}
          >
            <div className="grow">
              <div className="setting__label">{t('settings.resetTitle')}</div>
              <div className="setting__hint">{t('settings.resetHint')}</div>
            </div>
            {confirmReset ? (
              <div className="row" style={{ gap: 'var(--s-2)' }}>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    resetAll()
                    setConfirmReset(false)
                    onToast(t('toast.everythingReset'))
                  }}
                >
                  {t('settings.resetConfirm')}
                </Button>
                <Button variant="quiet" size="sm" onClick={() => setConfirmReset(false)}>
                  {t('common.cancel')}
                </Button>
              </div>
            ) : (
              <Button variant="danger" size="sm" onClick={() => setConfirmReset(true)}>
                {t('settings.reset')}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </>
  )
}
